import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { withTranslation } from 'react-i18next';
import LanguageSelect from '../shared/LanguageSelect';
import { resolveDocumentLanguage } from '../../utils/languages';
import { useUserStore } from '../../store';
import axios from 'axios';
import BillingSettingsDialog from './BillingSettingsDialog';
import { getInvoiceSplitMode, getCreateInvoiceLabelKey, buildDefaultInvoiceNotes } from '../../utils/invoiceSplit';
import { DEFAULT_VAT_RATE, roundMoney } from '../../utils/vat';

const formatCurrency = (amount) => {
  if (amount == null) return '-';
  return new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

const CreateInvoiceDialog = ({ open, handleClose, maintenance, onInvoiceCreated, t }) => {
  // Determine who the invoice goes to: billing customer if set, otherwise car owner
  const invoiceRecipient = maintenance?.billing_customer || maintenance?.customer;
  const hasBillingCustomer = !!maintenance?.billing_customer;
  // Split mode is decided in one place (utils/invoiceSplit.js) so the button label,
  // this preview and the backend agree. 'billing_customer' is the non-insurance split:
  // same arithmetic as the insurance gross split, but no insurance labels anywhere.
  const splitMode = getInvoiceSplitMode(maintenance);
  const isSplitMode = splitMode !== null;
  const isVatCompanyMode = splitMode === 'vat_company';
  const isInsuranceMode = splitMode === 'insurance' || splitMode === 'vat_company';
  const deductibleAmount = parseFloat(maintenance?.deductible_amount) || 0;
  const createLabel = t(getCreateInvoiceLabelKey(splitMode));

  const currentOrganization = useUserStore((state) => state.currentOrganization);
  const [language, setLanguage] = useState(() => resolveDocumentLanguage(invoiceRecipient, currentOrganization));
  // While false the language follows the recipient; a manual pick freezes it.
  const [languageTouched, setLanguageTouched] = useState(false);
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [billingDialogOpen, setBillingDialogOpen] = useState(false);

  // The invoice is written in the recipient's language: what the customer asked for,
  // otherwise the workshop's invoice language. Recomputed per opening because the same
  // dialog is reused for different customers. A manual pick wins over both.
  useEffect(() => {
    if (!open) {
      setLanguageTouched(false);
      return;
    }
    if (!languageTouched) {
      setLanguage(resolveDocumentLanguage(invoiceRecipient, currentOrganization));
    }
  }, [open, languageTouched, invoiceRecipient, currentOrganization]);

  // Pre-populate notes from description (if flagged) and customer notes flagged for invoice
  useEffect(() => {
    if (open && maintenance) {
      setNotes(buildDefaultInvoiceNotes(maintenance));
    }
  }, [open, maintenance]);

  // Calculate totals from maintenance, split by invoice_target
  const calculateTotals = () => {
    let totalInclVat = 0;
    let totalExclVat = 0;
    let totalVat = 0;
    let insuranceTotal = 0;
    let customerTotal = 0;
    let insuranceExclVat = 0;
    let insuranceVatTotal = 0;
    let insuranceItemCount = 0;
    let customerItemCount = 0;
    if (maintenance?.parts) {
      maintenance.parts.forEach(part => {
        const lineIncl = parseFloat(part.line_total_incl_vat) || parseFloat(part.line_total) || 0;
        const lineExcl = parseFloat(part.line_total) || 0;
        const lineVat = parseFloat(part.vat_amount) || 0;
        totalInclVat += lineIncl;
        totalExclVat += lineExcl;
        totalVat += lineVat;
        if (part.invoice_target === 'customer') {
          customerTotal += lineIncl;
          customerItemCount++;
        } else {
          insuranceTotal += lineIncl;
          insuranceExclVat += lineExcl;
          insuranceVatTotal += lineVat;
          insuranceItemCount++;
        }
      });
    }
    if (maintenance?.jobs) {
      maintenance.jobs.forEach(job => {
        const lineIncl = parseFloat(job.line_total_incl_vat) || parseFloat(job.line_total) || 0;
        const lineExcl = parseFloat(job.line_total) || 0;
        const lineVat = parseFloat(job.vat_amount) || 0;
        totalInclVat += lineIncl;
        totalExclVat += lineExcl;
        totalVat += lineVat;
        if (job.invoice_target === 'customer') {
          customerTotal += lineIncl;
          customerItemCount++;
        } else {
          insuranceTotal += lineIncl;
          insuranceExclVat += lineExcl;
          insuranceVatTotal += lineVat;
          insuranceItemCount++;
        }
      });
    }
    return { displaySubtotal: totalInclVat, totalInclVat, totalExclVat, totalVat, insuranceTotal, customerTotal, insuranceExclVat, insuranceVat: insuranceVatTotal, insuranceItemCount, customerItemCount };
  };

  const partsCount = maintenance?.parts?.length || 0;
  const jobsCount = maintenance?.jobs?.length || 0;
  const { displaySubtotal, totalInclVat, totalExclVat, totalVat, insuranceTotal, customerTotal, insuranceExclVat, insuranceVat, insuranceItemCount, customerItemCount } = calculateTotals();
  const itemCount = partsCount + jobsCount;

  // In ALV mode the backend routes lines by invoice_target: covered (insurance-targeted)
  // lines feed the formula; non-covered lines are billed whole to the customer. When no line
  // is flipped, covered == total.
  const coveredExclVat = insuranceExclVat;
  const coveredVat = insuranceVat;
  const hasLeasingCustomer = !!maintenance?.vat_invoice_customer;

  // VAT method "after_deductible" (Finvoice 1120442, CABAS "Omistaja ALV-velvollinen"): the
  // omavastuu comes off the covered gross and the insurer pays the rest without VAT. Mirrors
  // InvoiceService._create_vat_company_split_invoices.
  const isAfterDeductible = isVatCompanyMode && maintenance?.insurance_vat_method === 'after_deductible';
  // VAT company mode: the deductible exceeds the insurer's base (excl-VAT total, or the
  // covered gross with "after_deductible") → no insurance/leasing invoice is created, the
  // customer pays the full gross
  const isVatCompanyClamped = isAfterDeductible
    ? insuranceTotal > 0 && deductibleAmount >= insuranceTotal
    : isVatCompanyMode && coveredExclVat > 0 && deductibleAmount >= coveredExclVat;
  // Gross split: the omavastuu covers the whole billing-side share → the backend creates
  // only the car-owner invoice with every line and no omavastuu lines at all.
  const isGrossClamped = isSplitMode && !isVatCompanyMode && insuranceTotal > 0 && deductibleAmount >= insuranceTotal;

  // Split amounts
  let insuranceAmount, deductibleInvoiceAmount, leasingAmount = 0;
  if (isVatCompanyMode) {
    // Insurance pays covered excl VAT minus the NOMINAL deductible (clamped at 0).
    // The omavastuu is a fixed contractual amount — never divided by the VAT rate.
    insuranceAmount = isAfterDeductible
      ? Math.max(roundMoney((insuranceTotal - deductibleAmount) / (1 + DEFAULT_VAT_RATE / 100)), 0)
      : Math.max(coveredExclVat - deductibleAmount, 0);
    if (hasLeasingCustomer) {
      // Three-way: leasing company pays the covered VAT (with "after_deductible" the VAT of
      // the gross minus the omavastuu); the customer pays the deductible plus any
      // non-covered lines at full VAT.
      const leasingVat = isAfterDeductible ? roundMoney(insuranceTotal - deductibleAmount - insuranceAmount) : coveredVat;
      leasingAmount = isVatCompanyClamped ? 0 : leasingVat;
      deductibleInvoiceAmount = isVatCompanyClamped ? totalInclVat : (deductibleAmount + customerTotal);
    } else {
      // Two-way: the company pays the rest in one invoice (full VAT + deductible + non-covered)
      deductibleInvoiceAmount = totalInclVat - insuranceAmount;
    }
  } else if (isSplitMode) {
    // Gross split (insurance or plain billing customer): the omavastuu is deducted at
    // nominal value from the billing-side share and billed to the car owner.
    insuranceAmount = Math.max(insuranceTotal - deductibleAmount, 0);
    deductibleInvoiceAmount = customerTotal + Math.min(deductibleAmount, insuranceTotal);
  } else {
    insuranceAmount = totalInclVat;
    deductibleInvoiceAmount = 0;
  }

  const handleCreate = async () => {
    try {
      setCreating(true);
      setError(null);

      if (isSplitMode) {
        // Split invoice creation
        const response = await axios.post('/api/invoices/create-split/', {
          maintenance_id: maintenance.id,
          language,
          notes,
        });

        if (onInvoiceCreated && response.data.invoices?.length > 0) {
          // Navigate to the first invoice (insurance invoice)
          onInvoiceCreated(response.data.invoices[0]);
        }
      } else {
        // Regular single invoice creation
        const response = await axios.post('/api/invoices/', {
          maintenance_id: maintenance.id,
          language,
          notes,
        });

        if (onInvoiceCreated) {
          onInvoiceCreated(response.data);
        }
      }
      handleClose();
    } catch (err) {
      const errorMessage = err.response?.data?.error;
      let translatedError;
      if (errorMessage) {
        if (errorMessage.includes('already exists')) {
          translatedError = t('An invoice already exists for this maintenance. Cancel the existing invoice first.');
        } else if (errorMessage.includes('IBAN is required')) {
          setBillingDialogOpen(true);
          return;
        } else if (errorMessage.includes('not found')) {
          translatedError = t('Maintenance not found');
        } else if (errorMessage.includes('Customer data contains values that are too long')) {
          translatedError = t('Customer data is too long for invoice. Please fix the customer phone number and business ID.');
        } else {
          translatedError = errorMessage;
        }
      } else {
        translatedError = t('Failed to create invoice');
      }
      setError(translatedError);
    } finally {
      setCreating(false);
    }
  };

  const handleBillingSaved = () => {
    setBillingDialogOpen(false);
    handleCreate();
  };

  const handleCloseDialog = () => {
    if (!creating) {
      setError(null);
      setNotes('');
      handleClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ 'data-testid': 'create-invoice-dialog' }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ReceiptIcon color="primary" />
        {createLabel}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!maintenance?.customer && !maintenance?.billing_customer && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('No customer assigned to this maintenance. Please assign a customer first.')}
          </Alert>
        )}

        {isVatCompanyMode && maintenance?.customer?.type !== 'company' && !hasLeasingCustomer && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('vatDeductibleCompanyWarning')}
          </Alert>
        )}

        {isSplitMode && isVatCompanyMode ? (
          /* VAT Company mode: insurance at 0% VAT, company pays VAT portion */
          <>
            {isVatCompanyClamped && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('deductibleExceedsNetInfo')}
              </Alert>
            )}

            {/* Insurance Invoice Preview (0% VAT) */}
            {!isVatCompanyClamped && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(49, 130, 206, 0.05)', borderRadius: 2, mb: 2, border: '1px solid', borderColor: 'rgba(49, 130, 206, 0.2)' }}>
              <Typography variant="subtitle2" color="secondary.main" sx={{ mb: 1 }}>
                {t('Insurance invoice')} (ALV 0%)
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{t('Invoice recipient')}:</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">
                  {maintenance.billing_customer.name}
                </Typography>
              </Box>
              {maintenance.insurance_claim_number && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('Insurance claim number')}:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {maintenance.insurance_claim_number}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('Parts')} + {t('Jobs')}:</Typography>
                <Typography variant="body2" color="text.secondary">{itemCount} {t('items')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('Work total')} ({t('excl. VAT')}):</Typography>
                <Typography variant="body2">{formatCurrency(coveredExclVat)}</Typography>
              </Box>
              {deductibleAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {isAfterDeductible ? t('Deductible excl. VAT (customer\'s share)') : t('Deductible (customer\'s share)')}:
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    - {formatCurrency(isAfterDeductible ? coveredExclVat - insuranceAmount : deductibleAmount)}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" fontWeight={600}>{t('Total')}:</Typography>
                <Typography variant="body1" fontWeight={600} color="primary">
                  {formatCurrency(insuranceAmount)}
                </Typography>
              </Box>
            </Paper>
            )}

            {/* Leasing company VAT invoice (three-way split only) */}
            {!isVatCompanyClamped && hasLeasingCustomer && (
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(124, 58, 237, 0.06)', borderRadius: 2, mb: 2, border: '1px solid', borderColor: 'rgba(124, 58, 237, 0.25)' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#7C3AED' }}>
                  {t('Leasing company invoice (VAT)')}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{t('Invoice recipient')}:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {maintenance.vat_invoice_customer.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('VAT portion of repair')}:</Typography>
                  <Typography variant="body2">{formatCurrency(leasingAmount)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight={600}>{t('Total')}:</Typography>
                  <Typography variant="body1" fontWeight={600} sx={{ color: '#7C3AED' }}>
                    {formatCurrency(leasingAmount)}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Second invoice: three-way → customer (deductible + non-covered);
                two-way → company (full VAT + deductible) */}
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(245, 158, 11, 0.05)', borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'rgba(245, 158, 11, 0.2)' }}>
              <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                {hasLeasingCustomer ? t('Deductible invoice') : t('Company VAT invoice')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{t('Invoice recipient')}:</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {maintenance?.customer?.name || t('Not assigned')}
                </Typography>
              </Box>
              {hasLeasingCustomer ? (
                <>
                  {deductibleAmount > 0 && !isVatCompanyClamped && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{t('Deductible')}:</Typography>
                      <Typography variant="body2">{formatCurrency(deductibleAmount)}</Typography>
                    </Box>
                  )}
                  {customerTotal > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{t('Non-covered lines')}:</Typography>
                      <Typography variant="body2">{formatCurrency(customerTotal)}</Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('Work total')} ({t('incl. VAT')}):</Typography>
                    <Typography variant="body2">{formatCurrency(totalInclVat)}</Typography>
                  </Box>
                  {!isVatCompanyClamped && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{t('Insurance reimbursement')}:</Typography>
                      <Typography variant="body2" color="error.main">- {formatCurrency(insuranceAmount)}</Typography>
                    </Box>
                  )}
                </>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" fontWeight={600}>{t('Payable')}:</Typography>
                <Typography variant="body1" fontWeight={600} color="warning.main">
                  {formatCurrency(deductibleInvoiceAmount)}
                </Typography>
              </Box>
            </Paper>

            {/* Vehicle info */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, px: 1 }}>
              <Typography variant="body2" color="text.secondary">{t('Vehicle')}:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {maintenance?.vehicle?.registration_number}
              </Typography>
            </Box>
          </>
        ) : isSplitMode ? (
          /* Regular split mode: show two invoice previews */
          <>
            {isGrossClamped && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('deductibleExceedsTotalInfo')}
              </Alert>
            )}

            {/* Insurance / Billing Customer Invoice Preview */}
            {!isGrossClamped && (
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(49, 130, 206, 0.05)', borderRadius: 2, mb: 2, border: '1px solid', borderColor: 'rgba(49, 130, 206, 0.2)' }}>
              <Typography variant="subtitle2" color="secondary.main" sx={{ mb: 1 }}>
                {isInsuranceMode ? t('Insurance invoice') : t('Billing customer invoice')}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">{t('Invoice recipient')}:</Typography>
                <Typography variant="body2" fontWeight={600} color="success.main">
                  {maintenance.billing_customer.name}
                </Typography>
              </Box>
              {isInsuranceMode && maintenance.insurance_claim_number && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('Insurance claim number')}:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {maintenance.insurance_claim_number}
                  </Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('Parts')} + {t('Jobs')}:</Typography>
                <Typography variant="body2" color="text.secondary">{insuranceItemCount} {t('items')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('Work total')}:</Typography>
                <Typography variant="body2">{formatCurrency(insuranceTotal)}</Typography>
              </Box>
              {deductibleAmount > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{t('Deductible (customer\'s share)')}:</Typography>
                  <Typography variant="body2" color="error.main">- {formatCurrency(deductibleAmount)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body1" fontWeight={600}>{t('Total')}:</Typography>
                <Typography variant="body1" fontWeight={600} color="primary">
                  {formatCurrency(insuranceAmount)}
                </Typography>
              </Box>
            </Paper>
            )}

            {/* Deductible/Customer Invoice Preview */}
            {deductibleInvoiceAmount > 0 && (
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(245, 158, 11, 0.05)', borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'rgba(245, 158, 11, 0.2)' }}>
                <Typography variant="subtitle2" color="warning.main" sx={{ mb: 1 }}>
                  {isInsuranceMode ? t('Deductible invoice') : t('Customer invoice')}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{t('Invoice recipient')}:</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {maintenance?.customer?.name || t('Not assigned')}
                  </Typography>
                </Box>
                {!isGrossClamped && customerItemCount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('Customer-assigned lines')}:</Typography>
                    <Typography variant="body2">{customerItemCount} {t('items')} ({formatCurrency(customerTotal)})</Typography>
                  </Box>
                )}
                {isGrossClamped ? (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('Parts')} + {t('Jobs')}:</Typography>
                    <Typography variant="body2">{itemCount} {t('items')}</Typography>
                  </Box>
                ) : deductibleAmount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">{t('Deductible')}:</Typography>
                    <Typography variant="body2">{formatCurrency(deductibleAmount)}</Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight={600}>{t('Total')}:</Typography>
                  <Typography variant="body1" fontWeight={600} color="warning.main">
                    {formatCurrency(deductibleInvoiceAmount)}
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* Vehicle info */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, px: 1 }}>
              <Typography variant="body2" color="text.secondary">{t('Vehicle')}:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {maintenance?.vehicle?.registration_number}
              </Typography>
            </Box>
          </>
        ) : (
          /* Standard single invoice summary */
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', borderRadius: 2, mb: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('Invoice summary')}
            </Typography>

            {hasBillingCustomer ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{t('Invoice recipient')}:</Typography>
                  <Typography variant="body2" fontWeight={600} color="success.main">
                    {maintenance.billing_customer.name}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">{t('Car owner')}:</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {maintenance?.customer?.name || t('Not assigned')}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">{t('Customer')}:</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {maintenance?.customer?.name || t('Not assigned')}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">{t('Vehicle')}:</Typography>
              <Typography variant="body2" fontWeight={500}>
                {maintenance?.vehicle?.registration_number}
              </Typography>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">{t('Parts')}:</Typography>
              <Typography variant="body2">{partsCount} {t('items')}</Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">{t('Jobs')}:</Typography>
              <Typography variant="body2">{jobsCount} {t('items')}</Typography>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {parseFloat(maintenance?.order_discount_total || 0) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">{t('Subtotal')}:</Typography>
                <Typography variant="body2">
                  {formatCurrency(displaySubtotal)}
                </Typography>
              </Box>
            )}

            {parseFloat(maintenance?.order_discount_total || 0) > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  {t('orderDiscount')}{parseFloat(maintenance?.discount_percent || 0) > 0 ? ` (${maintenance.discount_percent}%)` : ''}:
                </Typography>
                <Typography variant="body2" sx={{ color: 'success.main' }}>
                  −{formatCurrency(parseFloat(maintenance.order_discount_total))}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight={600}>{t('Total')}:</Typography>
              <Typography variant="body1" fontWeight={600} color="primary">
                {formatCurrency(maintenance?.total_with_vat ? parseFloat(maintenance.total_with_vat) : totalInclVat)}
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Invoice Options */}
        <LanguageSelect
          fullWidth
          sx={{ mb: 2 }}
          value={language}
          label={t('Invoice language')}
          onChange={(e) => {
            setLanguageTouched(true);
            setLanguage(e.target.value);
          }}
        />

        <TextField
          label={t('Invoice notes')}
          multiline
          rows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('Optional notes to include on the invoice')}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleCloseDialog} disabled={creating}>
          {t('Cancel')}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={creating || (!maintenance?.customer && !maintenance?.billing_customer)}
          startIcon={creating ? <CircularProgress size={20} /> : <ReceiptIcon />}
        >
          {creating
            ? <span>{t('Creating...')}</span>
            : <span>{createLabel}</span>}
        </Button>
      </DialogActions>

      <BillingSettingsDialog
        open={billingDialogOpen}
        onClose={() => setBillingDialogOpen(false)}
        onSaved={handleBillingSaved}
      />
    </Dialog>
  );
};

export default withTranslation()(CreateInvoiceDialog);
